import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Monitor, FileEdit, Wrench, FileText, Inbox, ClipboardList,
} from "lucide-react";

const TYPE_META = {
  it_support:  { label: "IT",        icon: Monitor, color: "text-blue-600" },
  stationary:  { label: "Stationary", icon: FileEdit, color: "text-amber-700" },
  facility:    { label: "Facility",  icon: Wrench, color: "text-emerald-700" },
  hr_request:  { label: "HR",        icon: FileText, color: "text-red-700" },
};

const STATUS_TONES = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  awaiting_approval: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  assigned: "bg-indigo-50 text-indigo-700 border-indigo-200",
  in_progress: "bg-purple-50 text-purple-700 border-purple-200",
  pending_info: "bg-orange-50 text-orange-700 border-orange-200",
  pending_purchase: "bg-orange-50 text-orange-700 border-orange-200",
  ready_for_collection: "bg-teal-50 text-teal-700 border-teal-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-slate-50 text-slate-700 border-slate-200",
  cancelled: "bg-slate-50 text-slate-500 border-slate-200",
  on_hold: "bg-amber-50 text-amber-700 border-amber-200",
};

const PRIORITY_TONES = {
  urgent: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-emerald-100 text-emerald-700",
};

const HANDLER_ROLES = ["super_admin", "hr", "ea", "manager", "hod", "senior_management"];

export default function RequestList({ mode = "mine" }) {
  // mode: "mine" → portal view (My Requests); "admin" → handler dashboard
  const [me, setMe] = useState(null);
  const [scope, setScope] = useState(mode === "admin" ? "all" : "mine");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ scope });
      if (filterType) params.append("request_type", filterType);
      if (filterStatus) params.append("status", filterStatus);
      const { data } = await api.get(`/requests?${params.toString()}`);
      setRows(data || []);
      const { data: s } = await api.get("/requests/stats");
      setStats(s);
    } catch (e) { console.error("[RequestList] refresh failed", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [scope, filterType, filterStatus]);

  const isHandler = me && HANDLER_ROLES.includes(me.role);
  const bucket = mode === "admin" ? stats?.all : stats?.mine;

  return (
    <div className="space-y-4" data-testid={`requests-${mode}`}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">{mode === "admin" ? "Service Requests" : "My Requests"}</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            {mode === "admin" ? "Service Request Management" : "My Requests"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {mode === "admin" ? "Handle, reply, and route IT / Stationary / Facility / HR requests." : "Track all requests you've raised."}
          </p>
        </div>
        {mode !== "admin" && (
          <Button asChild className="brand-bg brand-bg-hover text-white" data-testid="new-request-btn">
            <Link to="/my-portal/requests/new"><Plus className="h-4 w-4 mr-2" /> New Request</Link>
          </Button>
        )}
      </div>

      {/* Stats strip */}
      {bucket && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="requests-stats">
          <Tile label="Total" value={bucket.total || 0} accent="slate" testid="stat-total" />
          <Tile label="Open" value={bucket.open || 0} accent="blue" testid="stat-open" />
          <Tile label="Overdue" value={bucket.overdue || 0} accent="red" testid="stat-overdue" />
          <Tile label="Closed" value={bucket.closed || 0} accent="emerald" testid="stat-closed" />
        </div>
      )}

      {/* Scope tabs (admin) */}
      {mode === "admin" && isHandler && (
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList data-testid="scope-tabs">
            <TabsTrigger value="all"><ClipboardList className="h-3.5 w-3.5 mr-1.5" /> All</TabsTrigger>
            <TabsTrigger value="team"><ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Team</TabsTrigger>
            <TabsTrigger value="mine"><Inbox className="h-3.5 w-3.5 mr-1.5" /> My own</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterType || "_all"} onValueChange={(v) => setFilterType(v === "_all" ? "" : v)}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-type">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All types</SelectItem>
            <SelectItem value="it_support">IT Support</SelectItem>
            <SelectItem value="stationary">Stationary</SelectItem>
            <SelectItem value="facility">Facility</SelectItem>
            <SelectItem value="hr_request">HR Request</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus || "_all"} onValueChange={(v) => setFilterStatus(v === "_all" ? "" : v)}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All statuses</SelectItem>
            {["new","awaiting_approval","approved","assigned","in_progress","pending_info","ready_for_collection","resolved","closed","rejected","cancelled"].map((s) => (
              <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="empty-state">
            {mode === "admin" ? "No requests to display." : "You haven't raised any requests yet. Click + New Request to start."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Subject</th>
                {mode === "admin" && <th className="px-3 py-2 text-left">Employee</th>}
                <th className="px-3 py-2 text-left">Priority</th>
                <th className="px-3 py-2 text-left">Cost</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meta = TYPE_META[r.request_type] || {};
                const Icon = meta.icon || ClipboardList;
                const target = mode === "admin" ? `/admin/requests/${r.id}` : `/my-portal/requests/${r.id}`;
                return (
                  <tr
                    key={r.id}
                    className="border-t border-slate-100 hover:bg-red-50/30 cursor-pointer"
                    onClick={() => navigate(target)}
                    data-testid={`request-row-${r.request_number}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.request_number}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <Icon className={`h-3.5 w-3.5 ${meta.color || ""}`} />
                        {meta.label || r.request_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 max-w-xs truncate">
                      <div className="font-medium text-slate-900 truncate">{r.subject}</div>
                      <div className="text-[10px] text-slate-400">{r.category || "—"}</div>
                    </td>
                    {mode === "admin" && (
                      <td className="px-3 py-2 text-xs">
                        <div className="font-medium">{r.employee_name}</div>
                        <div className="text-[10px] text-slate-400">{r.employee_punch_id}</div>
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${PRIORITY_TONES[r.priority] || ""}`}>
                        {r.priority}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.estimated_cost ? `₹${Number(r.estimated_cost).toLocaleString("en-IN")}` : <span className="text-slate-400">—</span>}
                      {r.cost_category && r.cost_category !== "no_cost" && r.cost_category !== "auto_approved" && (
                        <div className="text-[10px] text-slate-400">{r.cost_category.replace(/_/g, " ")}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${STATUS_TONES[r.status] || ""}`}>
                        {(r.status || "").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, accent = "slate", testid }) {
  const tone = {
    slate: "border-slate-100 bg-slate-50/40 text-slate-700",
    blue: "border-blue-100 bg-blue-50/40 text-blue-700",
    red: "border-red-100 bg-red-50/40 text-red-700",
    emerald: "border-emerald-100 bg-emerald-50/40 text-emerald-700",
  }[accent];
  return (
    <div className={`rounded-md border ${tone} px-3 py-3 text-center`} data-testid={testid}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}
